import React, { Component } from 'react';
import styled from 'styled-components';
import {
  makeExternalUrl,
  attachShopSlug,
  attachLocale,
  makeDomId,
  trackAnalyticsNew,
  resolveAccessibleLabel,
} from '../../../../lib/utils';
import HeartIcon from '../../../svgs/HeartIcon';
import CardHoverState from '../shared/CardHoverState';
import {
  GRID,
  DOM_SHOP_CARD,
  DATA_TRACK_ROLE_CONTAINER,
  DATA_TRACK_TYPE_CTA,
} from '../../../../lib/constants';
import MilesOffer from '../shared/MilesOffer';
import PromoBadge from '../PromoBadge/PromoBadge';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';

const makeCardSize = (viewType) => {
  if (viewType === GRID) {
    return (p) => p.theme.unit(32.75);
  }
  return (p) => p.theme.unit(26.25);
};
const ShopCardDiv = styled.article`
  position: relative;
  background-color: ${(p) => p.theme.c.bg.primary};
  width: ${(p) => makeCardSize(p.viewType)};
  box-shadow: 0 2px 8px 0 ${({ theme }) => theme.c.border['box-shadow']};
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  :hover,
  :focus {
    box-shadow: 0 8px 16px ${({ theme }) => theme.c.border['box-shadow']};
  }
`;

const MobileStoreInfoAnchor = styled.a`
  display: block;
  text-decoration: none;
`;

const CardTagSpan = styled.span`
  display: none;
`;

const MobileShopCardDiv = styled(ShopCardDiv)`
  width: ${(p) => p.theme.unit(36)};
`;

const CardHeader = styled.header`
  position: absolute;
  padding-top: 14px;
  padding-left: 14px;
`;

// *** Card Body ***
const CardBody = styled.section`
  height: 168px;
  position: relative;
`;

const CardImgContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  text-decoration: none;
`;

const CardImg = styled.img`
  max-width: ${(p) => p.theme.unit(16)};
  max-height: ${(p) => p.theme.unit(8)};
`;

// *** Card Footer ***
const CardFooter = styled.footer`
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: end;
  padding: 16px 12px;
  border-top: 1px solid ${(p) => p.theme.c.border.primary};
`;

const HeartIconButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.c.icon.interactive};
  &:hover {
    path.heart-icon {
      ${(p) => (p.isFavourite && 'fill: none;')}
      stroke-width: 3px;
    }
  }
`;

const emptyCallback = () => {};

// TODO: make into a dumb component
class ShopCard extends Component {
  constructor(props) {
    super(props);

    this.favouriteButton;
  }

  componentDidMount() {
    // After mounting, set focus to favourite button if applicable
    this.props.pageStateModifiers
      && this.props.pageStateModifiers.setElementFocus
      && this.props.pageStateModifiers.setElementFocus(
        this.props.pageContext,
        this.props.id,
        this.favouriteButton
      );
  }

  render() {
    const {
      id,
      displayName,
      slug,
      active,
      affiliateUrl,
      recencyOfOffersDays,
      baseMiles,
      amountPerBaseMiles,
      mediumImageUrl,
      mediumImageAltText,
      exclusionsApply,
      imageTitleTag,
      multiplier,
      viewType = 'Carousel',
      isFavourite,
      removeItemHandler = emptyCallback,
      saveItemHandler = emptyCallback,
      pageStateModifiers = {},
      locale,
      tags,
      dataLayerId,
      dataContextContainer,
      dataIndex,
      idPrefix,
      pageContext,
      linkTextPrepositions,
      siteWideMultiplier,
      trackingLocation = 'store-card:',
    } = this.props;

    const {
      syncUserDataOnPage = emptyCallback,
      isUserLoggedIn = emptyCallback,
      makeAndOpenSignInModal = emptyCallback,
      setActiveFocus = emptyCallback,
    } = pageStateModifiers;

    let secondaryLink = attachShopSlug(slug);
    secondaryLink = attachLocale(secondaryLink, locale);

    const config = this.props.config || {};

    const {
      singularMilesText = '',
      pluralMilesText = '',
      singularFlatRateMilesText = '',
      pluralFlatRateMilesText = '',
      exclusionsApplyText = '',
      primaryText = '',
      secondaryText = '',
      milesBadgeText = '',
      primaryButtonTagSet = {},
      secondaryButtonTagSet = {},
      extraMilesText = '',
    } = config;

    const favouriteAddLabel = config.favouriteAddLabel.replace('%s', displayName) || '';
    const favouriteRemoveLabel = config.favouriteRemoveLabel.replace('%s', displayName) || '';
    const shopTrackingLocation = `${trackingLocation}${slug}`;
    const storeInfoTrack = trackAnalyticsNew({
      url: secondaryLink,
      location: shopTrackingLocation,
      name: 'store-info',
      tags: secondaryButtonTagSet,
    });
    const favouriteTrack = trackAnalyticsNew({
      url: '',
      location: shopTrackingLocation,
      name: isFavourite ? 'unsave' : 'save',
    });
    let StarIcon = null;

    // *** HANDLER ***
    const removeShopHandler = () => {
      if (isUserLoggedIn()) {
        removeItemHandler(id, () => {
          setActiveFocus(pageContext, id);
          syncUserDataOnPage();
        });
      } else {
        makeAndOpenSignInModal();
      }
    };

    const saveShopHandler = () => {
      if (isUserLoggedIn()) {
        saveItemHandler(id, active, () => {
          setActiveFocus(pageContext, id);
          syncUserDataOnPage();
        });
      } else {
        makeAndOpenSignInModal();
      }
    };

    StarIcon = ({ isMobile }) => (
      <HeartIconButton
        isFavourite={isFavourite}
        aria-label={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
        title={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
        data-track-id={favouriteTrack.dataTrackingId}
        data-track-click={favouriteTrack.dataTrack}
        data-track-type={DATA_TRACK_TYPE_CTA}
        innerRef={(element) => (this.favouriteButton = element)}
        onClick={() => {
          isFavourite ? removeShopHandler(isMobile) : saveShopHandler(isMobile);
        }}
      >
        <HeartIcon active={isFavourite} />
      </HeartIconButton>
    );

    const DesktopBody = () => {
      const domId = makeDomId(DOM_SHOP_CARD, slug);

      return (
        <ShopCardDiv
          className="hover-container shop-card__wrapper"
          viewType={viewType}
          id={idPrefix ? `${idPrefix}_${domId}` : domId}
          data-layer-source-id={dataLayerId}
          data-context-container={dataContextContainer}
          data-index={dataIndex}
          pageStateModifiers={pageStateModifiers}
          dataLayerId={dataLayerId}
          dataContextContainer={dataContextContainer}
          shopSlug={slug}
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          useDefaultOnClickHandler={false}
        >
          {Boolean(multiplier) && (
            <CardHeader>
              <PromoBadge
                extraMilesText={extraMilesText}
                pluralFlatRateMilesText={pluralFlatRateMilesText}
              />
            </CardHeader>
          )}
          <CardTagSpan
            id={idPrefix ? `${idPrefix}_${slug}_tag` : `${slug}_tag`}
          >
            {displayName}
          </CardTagSpan>
          <CardBody>
            <div className="show-hover">
              <CardHoverState
                parentId={domId}
                tags={tags}
                secondaryLink={secondaryLink}
                primaryText={primaryText}
                primaryButtonTagSet={primaryButtonTagSet}
                secondaryText={secondaryText}
                secondaryButtonTagSet={secondaryButtonTagSet}
                dataLayerId={dataLayerId}
                dataContextContainer={dataContextContainer}
                pageStateModifiers={pageStateModifiers}
                imageTitleTag={imageTitleTag}
                locale={locale}
                shopSlug={slug}
                shop={{ displayName, linkTextPrepositions }}
                defaultOnClickHandler={false}
                idPrefix={idPrefix}
                trackingLocation={shopTrackingLocation}
                primaryTrackingName="shop-now"
                secondaryTrackingName="store-info"
              />
            </div>
            <CardImgContainer>
              <CardImg
                src={makeExternalUrl(mediumImageUrl)}
                alt={mediumImageAltText}
              />
            </CardImgContainer>
          </CardBody>
          <CardFooter row={exclusionsApply}>
            <MilesOffer
              multiplier={multiplier}
              siteWideMultiplier={siteWideMultiplier}
              baseMiles={baseMiles}
              amountPerBaseMiles={amountPerBaseMiles}
              singularMilesText={singularMilesText}
              pluralMilesText={pluralMilesText}
              singularFlatRateMilesText={singularFlatRateMilesText}
              pluralFlatRateMilesText={pluralFlatRateMilesText}
              milesBadgeText={milesBadgeText}
              locale={locale}
            />
            <StarIcon />
          </CardFooter>
        </ShopCardDiv>
      );
    };

    const MobileBody = () => {
      const domId = makeDomId(DOM_SHOP_CARD, slug, true);
      const storeInfoLabel = resolveAccessibleLabel(
        null,
        { displayName },
        linkTextPrepositions,
        'for',
        secondaryText
      );

      return (
        <MobileShopCardDiv
          id={domId}
          data-layer-source-id={dataLayerId}
          data-context-container={dataContextContainer}
          data-index={dataIndex}
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          className="shop-card__wrapper"
        >
          <MobileStoreInfoAnchor
            href={storeInfoTrack.url}
            aria-label={storeInfoLabel}
            data-track-id={storeInfoTrack.dataTrackingId}
            data-track-click={storeInfoTrack.dataTrack}
            data-track-type={DATA_TRACK_TYPE_CTA}
            data-layer-source-id={dataLayerId}
            data-context-container={dataContextContainer}
            data-index={dataIndex}
            data-track-role={DATA_TRACK_ROLE_CONTAINER}
          >
            {Boolean(multiplier) && (
              <CardHeader>
                <PromoBadge
                  extraMilesText={extraMilesText}
                  pluralFlatRateMilesText={pluralFlatRateMilesText}
                />
              </CardHeader>
            )}
            <CardTagSpan id={`${slug}_tag`}>{displayName}</CardTagSpan>
            <CardBody title={imageTitleTag}>
              <CardImgContainer>
                <CardImg
                  src={makeExternalUrl(mediumImageUrl)}
                  alt={mediumImageAltText}
                />
              </CardImgContainer>
            </CardBody>
          </MobileStoreInfoAnchor>
          <CardFooter row={exclusionsApply}>
            <MilesOffer
              multiplier={multiplier}
              siteWideMultiplier={siteWideMultiplier}
              baseMiles={baseMiles}
              amountPerBaseMiles={amountPerBaseMiles}
              singularMilesText={singularMilesText}
              pluralMilesText={pluralMilesText}
              singularFlatRateMilesText={singularFlatRateMilesText}
              pluralFlatRateMilesText={pluralFlatRateMilesText}
              milesBadgeText={milesBadgeText}
              locale={locale}
            />
            <StarIcon />
          </CardFooter>
        </MobileShopCardDiv>
      );
    };

    const renderComp = (
      <MediaQueryHelper
        desktopChildren={DesktopBody()}
        mobileChildren={MobileBody()}
      />
    );
    return renderComp;
  }
}

export default ShopCard;
